import axios from './axios';
import { getUser } from './auth';

// Current property and inspector/supervisor context
let currentInspectorId = null;
let currentSupervisorId = null;
let currentPropertyId = null;

// Track initialization state
let isInitialized = false;

// Track last action to prevent duplicates
let lastTrackedAction = {
  actionName: null,
  propertyId: null,
  detailsHash: null,
  additionalFieldsHash: null,
  timestamp: 0
};
const DUPLICATE_WINDOW_MS = 5000; // 5 second window to prevent duplicates

// Helper function to create a simple hash from any data
const hashData = (data) => {
  try {
    return JSON.stringify(data);
  } catch (e) {
    return String(data);
  }
};

export const setCurrentInspectorId = (id) => {
  currentInspectorId = id;
};

export const setCurrentSupervisorId = (id) => {
  currentSupervisorId = id;
};

export const setCurrentPropertyId = (id) => {
  currentPropertyId = id;
};

// Get browser info
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  
  if (ua.indexOf("Firefox") > -1) browser = "Firefox";
  else if (ua.indexOf("Chrome") > -1 && ua.indexOf("Edg") === -1) browser = "Chrome";
  else if (ua.indexOf("Edg") > -1) browser = "Edge";
  else if (ua.indexOf("Safari") > -1) browser = "Safari";
  else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browser = "Opera";
  
  return browser;
};

// Get IP address (cached so we only fetch once per session)
let _cachedIP = null;
const getIPAddress = async () => {
  if (_cachedIP) return _cachedIP;
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    _cachedIP = data.ip;
    return _cachedIP;
  } catch (err) {
    _cachedIP = "Unknown";
    return _cachedIP;
  }
};

// Track a login
export const trackLogin = async (userId, role = 'inspector', comment = null) => {
  if (role === 'supervisor') {
    setCurrentSupervisorId(userId);
  } else {
    setCurrentInspectorId(userId);
  }
  try {
    const page_url = window.location.href;
    const browser = getBrowserInfo();
    const user_agent = navigator.userAgent;
    const ip_address = await getIPAddress();

    console.log(`📝 Tracking login for ${role}:`, userId);

    // Track single log_in event
    const payload = {
      element_clicked: 'log_in',
      page_url,
      property_id: null,
      browser,
      ip_address,
      user_agent,
      comment
    };
    if (role === 'supervisor') {
      payload.supervisor_id = userId;
    } else {
      payload.inspector_id = userId;
    }
    await axios.post('/click/record', payload);
    console.log('✅ Login tracked successfully!');
  } catch (err) {
    console.error('❌ Failed to track login:', err);
  }
};

// Track a logout
export const trackLogout = async (userId, role = 'inspector', comment = null) => {
  try {
    const page_url = window.location.href;
    const browser = getBrowserInfo();
    const user_agent = navigator.userAgent;
    const ip_address = await getIPAddress();

    console.log(`📝 Tracking logout for ${role}:`, userId);

    // Track single log_out event
    const payload = {
      element_clicked: 'log_out',
      page_url,
      property_id: null,
      browser,
      ip_address,
      user_agent,
      comment
    };
    if (role === 'supervisor') {
      payload.supervisor_id = userId;
    } else {
      payload.inspector_id = userId;
    }
    await axios.post('/click/record', payload);
    console.log('✅ Logout tracked successfully!');
  } catch (err) {
    console.error('❌ Failed to track logout:', err);
  }
};

export const FEATURE_LABELS = {
  floor: 'Number of Floors',
  landuse: 'Land Use',
  land_use: 'Land Use',
  landarea: 'Land Area',
  land_area: 'Land Area',
  coveredarea: 'Covered Area',
  covered_area: 'Covered Area',
  pt: 'PT-5 Serial Number',
};

const ROW_EXPLANATION_LABELS = {
  floor: 'Number of Floors',
  landuse: 'Land Use',
  landarea: 'Land Area',
  coveredarea: 'Covered Area',
};

export const collectFeatureChanges = (features, initialAfterValues, options = {}) => {
  const changes = [];
  const valueField = options.valueField || 'after';

  (features || []).forEach((feature) => {
    const val = feature[valueField]?.toString().trim();
    const initialVal = options.getInitialVal
      ? options.getInitialVal(feature)
      : (initialAfterValues?.[feature.key]?.toString().trim() ?? feature.before?.toString().trim());
    const isModified = val !== initialVal;
    if (!isModified) return;

    const displayName = FEATURE_LABELS[feature.key] || feature.label || feature.key;
    let finalVal = val;
    if (options.getFinalVal) {
      finalVal = options.getFinalVal(feature, val, initialVal, isModified);
    }

    changes.push(
      `${displayName} changed from "${initialVal || 'Not set'}" to "${finalVal || 'Not set'}"`
    );
  });

  return changes;
};

export const collectInspectorText = ({
  notReassessReason,
  overallExplanation,
  rowExplanations,
  features,
  freeTextComment,
} = {}) => {
  const parts = [];

  if (notReassessReason?.trim()) {
    parts.push(notReassessReason.trim());
  }
  if (freeTextComment?.trim()) {
    parts.push(freeTextComment.trim());
  }
  if (overallExplanation?.trim()) {
    parts.push(`Overall Explanation: ${overallExplanation.trim()}`);
  }

  if (rowExplanations) {
    Object.entries(rowExplanations).forEach(([key, text]) => {
      if (text?.trim()) {
        parts.push(`${ROW_EXPLANATION_LABELS[key] || key} explanation: ${text.trim()}`);
      }
    });
  }

  (features || []).forEach((feature) => {
    if (feature.explanation?.trim()) {
      const label = feature.label || FEATURE_LABELS[feature.key] || feature.key;
      parts.push(`${label} explanation: ${feature.explanation.trim()}`);
    }
  });

  return parts.join('; ');
};

export const buildClickDetails = ({ featureChanges = [], inspectorText = '' } = {}) => {
  let featureChangesText = '';
  if (Array.isArray(featureChanges)) {
    featureChangesText = featureChanges.length ? featureChanges.join('; ') : '';
  } else if (typeof featureChanges === 'string') {
    featureChangesText = featureChanges;
  }

  const inspectorTextValue = inspectorText?.trim() || '';
  const comment = [featureChangesText, inspectorTextValue].filter(Boolean).join('; ') || null;

  return {
    comment,
    inspector_text: inspectorTextValue || null,
    feature_changes: featureChangesText || null,
  };
};

const normalizeTrackDetails = (details) => {
  if (!details) {
    return { comment: null, inspector_text: null, feature_changes: null };
  }

  if (typeof details === 'string') {
    if (/ changed from "/.test(details)) {
      const parts = details.split('; ').map((part) => part.trim()).filter(Boolean);
      const featureParts = parts.filter((part) => / changed from "/.test(part));
      const textParts = parts.filter((part) => !/ changed from "/.test(part) && part !== 'No feature changes');
      return {
        comment: details,
        inspector_text: textParts.join('; ') || null,
        feature_changes: featureParts.length
          ? featureParts.join('; ')
          : (parts.includes('No feature changes') ? 'No feature changes' : null),
      };
    }

    return {
      comment: details,
      inspector_text: details,
      feature_changes: null,
    };
  }

  if (details.featureChanges || details.feature_changes || details.inspectorText || details.inspector_text) {
    return buildClickDetails({
      featureChanges: details.featureChanges ?? details.feature_changes ?? [],
      inspectorText: details.inspectorText ?? details.inspector_text ?? '',
    });
  }

  return {
    comment: details.comment ?? null,
    inspector_text: details.inspectorText ?? details.inspector_text ?? null,
    feature_changes: details.featureChanges ?? details.feature_changes ?? null,
  };
};

// Track an action
export const trackAction = async (actionName, propertyId = null, details = null, additionalFields = {}, role = null) => {
  try {
    const now = Date.now();
    const detailsHash = hashData(details);
    const additionalFieldsHash = hashData(additionalFields);
    
    // Check for duplicate action
    if (
      lastTrackedAction.actionName === actionName &&
      lastTrackedAction.propertyId === propertyId &&
      lastTrackedAction.detailsHash === detailsHash &&
      lastTrackedAction.additionalFieldsHash === additionalFieldsHash &&
      (now - lastTrackedAction.timestamp) < DUPLICATE_WINDOW_MS
    ) {
      console.log('Skipping duplicate action:', actionName);
      return;
    }

    // Update last tracked action
    lastTrackedAction = {
      actionName,
      propertyId,
      detailsHash,
      additionalFieldsHash,
      timestamp: now
    };

    const user = getUser();
    const page_url = window.location.href;
    const browser = getBrowserInfo();
    const user_agent = navigator.userAgent;
    const ip_address = await getIPAddress();
    
    const userRole = role || (user && user.role) || 'inspector';
    let inspector_id = null;
    let supervisor_id = null;
    
    if (userRole === 'supervisor') {
      supervisor_id = currentSupervisorId || (user && user.id) || null;
    } else {
      inspector_id = currentInspectorId || (user && user.id) || null;
    }
    
    const prop_id = propertyId || currentPropertyId || null;
    const payload = normalizeTrackDetails(details);

    console.log('Tracking action:', {
      element_clicked: actionName,
      page_url,
      inspector_id,
      supervisor_id,
      property_id: prop_id,
      browser,
      ip_address,
      ...payload,
      ...additionalFields,
    });

    await axios.post('/click/record', {
      element_clicked: actionName,
      page_url,
      inspector_id,
      supervisor_id,
      property_id: prop_id,
      browser,
      ip_address,
      user_agent,
      comment: payload.comment,
      inspector_text: payload.inspector_text,
      feature_changes: payload.feature_changes,
      old_value: additionalFields.old_value || null,
      new_value: additionalFields.new_value || null,
      searched_pin: additionalFields.searched_pin || null,
      comment_text: additionalFields.comment_text || null,
      star_action: additionalFields.star_action || null,
      explanation: additionalFields.explanation || null,
      overall_explanation: additionalFields.overall_explanation || null,
      floor_explanation: additionalFields.floor_explanation || null,
      landuse_explanation: additionalFields.landuse_explanation || null,
      landarea_explanation: additionalFields.landarea_explanation || null,
      coveredarea_explanation: additionalFields.coveredarea_explanation || null,
    });

    console.log('Action tracked successfully!');
  } catch (err) {
    console.error('Failed to track action:', err);
  }
};

// Initialize click listener
let lastDelegatedClick = { actionName: null, propertyId: null, timestamp: 0 };
const DELEGATED_CLICK_DEBOUNCE_MS = 800;

export const initClickTracking = () => {
  if (isInitialized) {
    console.log('Click tracking already initialized, skipping...');
    return;
  }
  console.log('Click tracking initializing...');
  document.addEventListener('click', (e) => {
    const target = e.target.closest('[data-click-id]');
    if (target) {
      const actionName = target.getAttribute('data-click-id');
      const propertyId = target.getAttribute('data-property-id');
      const now = Date.now();

      if (
        lastDelegatedClick.actionName === actionName &&
        lastDelegatedClick.propertyId === propertyId &&
        (now - lastDelegatedClick.timestamp) < DELEGATED_CLICK_DEBOUNCE_MS
      ) {
        console.log('Ignoring rapid duplicate click on:', actionName);
        return;
      }
      lastDelegatedClick = { actionName, propertyId, timestamp: now };

      console.log('Click detected on element with data-click-id:', actionName, 'and data-property-id:', propertyId);
      trackAction(actionName, propertyId);
    }
  }, { passive: true });
  isInitialized = true;
  console.log('Click tracking initialized successfully!');
};

// Export click tracking data in the requested format
export const exportClickTrackingData = async (inspectorId = null, rawOnly = false) => {
  try {
    const url = inspectorId 
      ? `/click/export?inspector_id=${inspectorId}` 
      : '/click/export';
    const response = await axios.get(url, {
      params: rawOnly ? { raw_only: '1' } : {},
      timeout: 180000,
    });
    return response.data;
  } catch (err) {
    console.error('Failed to export click tracking data:', err);
    throw err;
  }
};

// Delete click tracking data
export const deleteClickTrackingData = async (inspectorId = null) => {
  try {
    const url = inspectorId 
      ? `/click/delete?inspector_id=${inspectorId}` 
      : '/click/delete';
    const response = await axios.delete(url);
    return response.data;
  } catch (err) {
    console.error('Failed to delete click tracking data:', err);
    throw err;
  }
};

// Export supervisor click tracking data
export const exportSupervisorClickTrackingData = async (supervisorId = null, rawOnly = false) => {
  try {
    const url = supervisorId 
      ? `/click/export/supervisor?supervisor_id=${supervisorId}` 
      : '/click/export/supervisor';
    const response = await axios.get(url, {
      params: rawOnly ? { raw_only: '1' } : {},
      timeout: 180000,
    });
    return response.data;
  } catch (err) {
    console.error('Failed to export supervisor click tracking data:', err);
    throw err;
  }
};

// Delete supervisor click tracking data
export const deleteSupervisorClickTrackingData = async (supervisorId = null) => {
  try {
    const url = supervisorId 
      ? `/click/delete/supervisor?supervisor_id=${supervisorId}` 
      : '/click/delete/supervisor';
    const response = await axios.delete(url);
    return response.data;
  } catch (err) {
    console.error('Failed to delete supervisor click tracking data:', err);
    throw err;
  }
};
